import Service, { type Registry as Services, service } from '@ember/service';
import config from 'uplisting-frontend/config/environment';
import { setGlobalTranslations } from 'uplisting-frontend/utils/translations';
import { setValidationMessages } from 'uplisting-frontend/validations/messages';

export interface ISupportedLocale {
  code: string;
  label: string;
}

export const DEFAULT_LOCALE = 'en-gb';

/**
 * Locales the app ships translations for. Labels are autonyms (each language in
 * its own name) and are intentionally NOT translated.
 */
export const SUPPORTED_LOCALES: ISupportedLocale[] = [
  { code: 'en-gb', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
];

const SUPPORTED_CODES = SUPPORTED_LOCALES.map(({ code }) => code);

export default class LocaleService extends Service {
  @service intl!: Services['intl'];
  @service storage!: Services['storage'];
  @service sentry!: Services['sentry'];

  // Locales whose translations have already been fetched + registered this session.
  private loadedLocales = new Set<string>();

  get supportedLocales(): ISupportedLocale[] {
    return SUPPORTED_LOCALES;
  }

  get activeLocale(): string {
    return this.intl.primaryLocale ?? DEFAULT_LOCALE;
  }

  /**
   * Resolve the locale to use on boot: a persisted user choice if there is one,
   * otherwise English. We intentionally do NOT auto-detect from the browser —
   * the language is opt-in via the picker and then remembered.
   */
  resolve(): string {
    const stored = this.storage.localePreference;

    if (stored && SUPPORTED_CODES.includes(stored)) {
      return stored;
    }

    return DEFAULT_LOCALE;
  }

  /**
   * Load (if needed), activate, persist, and apply a locale. Used both on boot
   * and from the language picker.
   */
  async setLocale(code: string): Promise<void> {
    const locale = SUPPORTED_CODES.includes(code) ? code : DEFAULT_LOCALE;

    await this.loadTranslations(locale);

    this.intl.setLocale(locale);
    this.storage.localePreference = locale;

    // Refresh locale-dependent caches (day names, country list, fee-rule labels)
    // and the changeset validation messages, so a switch translates them too
    // rather than leaving them in the boot language.
    setGlobalTranslations(this.intl);
    setValidationMessages(this.intl);

    window.document.documentElement.setAttribute('lang', locale);
  }

  private async loadTranslations(locale: string): Promise<void> {
    // Under test, translations are bundled and already loaded (publicOnly is off),
    // so there is nothing to fetch. Keyed off the build environment rather than
    // config.IS_TEST — some acceptance tests stub IS_TEST to false. See
    // config/ember-intl.js.
    if (config.environment === 'test') {
      return;
    }

    if (this.loadedLocales.has(locale)) {
      return;
    }

    // Each locale's JSON is self-contained (en-gb is merged in at build time), so
    // only the active locale is fetched. It is served at a stable, non-fingerprinted
    // path, so it is cache-busted with the build version.
    const url = `${config.rootURL}translations/${locale}.json?v=${config.buildVersion}`;

    try {
      const response = await window.fetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to load "${locale}" translations: ${response.status}`,
        );
      }

      // response.json() throws if an Amplify SPA rewrite returned index.html (HTML)
      // with a 200 status instead of the JSON file.
      const translations = await response.json();

      this.intl.addTranslations(locale, translations);
      this.loadedLocales.add(locale);
    } catch (error) {
      this.sentry.captureException(error);

      throw error;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    locale: LocaleService;
  }
}
